import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "./reduxHooks";
import { DiscountSliceAction } from "../redux/slice/discountSlice";
import { IDiscountResponse } from "../typings";
import { ICartSession } from "../typings/interfaces/pos/cart-session";
import { getItemTotal } from "../util/cart-totals";
import { sessionSpecialCharge } from "../util/special-charge";
import { CartItem } from "../typings/interfaces/pos/cart-item";
import {
  calculateDiscountAmount,
  getEligibleSessionAmount,
  resolveLockedDiscount,
} from "../util/discount-calc";

const useCart = () => {
  const dispatch = useAppDispatch();
  const { cart, selectedSession } = useAppSelector((state) => state.cart);
  const [selectedSessionDiscount, setSelectedSessionDiscount] = useState(0);
  const { customer } = useAppSelector((state) => state.customer);
  const {
    getDiscountByStoreResponse,
    eligibleDiscounts,
    selectedDiscount,
    customDiscount,
    discountRemoved,
    discountLocked,
    lockedDiscount,
  } = useAppSelector((state) => state.discount);
  const [totalDiscountAmount, setTotalDiscountAmount] = useState(0);
  const { configs: specialCharges } = useAppSelector((state) => state.specialCharge);

  // An item's total in its session: a Special ticket adds its Special charge
  const itemSpecialCharge = (session: ICartSession | null | undefined, item: CartItem) =>
    sessionSpecialCharge(item, session?.isSpecial, specialCharges);
  const itemTotalIn = (session: ICartSession | null | undefined, item: CartItem) =>
    getItemTotal(item, itemSpecialCharge(session, item));

  const sessionTotal =
    selectedSession?.cartItems.reduce(
      (acc, item) => acc + itemTotalIn(selectedSession, item),
      0
    ) ?? 0;

  const cartTotal = useMemo(
    () =>
      cart.reduce((acc, session) => {
        return (
          acc +
          session.cartItems.reduce(
            (acc, item) => acc + itemTotalIn(session, item),
            0
          )
        );
      }, 0),
    [cart, specialCharges]
  );

  const getSessionTotalBySessionId = (sessionId: string) => {
    const session = cart.find((session) => session.sessionId === sessionId);
    return (
      session?.cartItems.reduce(
        (acc, item) => acc + itemTotalIn(session, item),
        0
      ) ?? 0
    );
  };
  const getSessionTotalBySession = (session: ICartSession) => {
    return (
      session.cartItems.reduce(
        (acc, item) => acc + itemTotalIn(session, item),
        0
      ) ?? 0
    );
  };

  // The Special charge across every ticket in the cart (0 if all Regular)
  const cartSpecialCharge = cart.reduce(
    (acc, session) =>
      acc + session.cartItems.reduce((sum, item) => sum + itemSpecialCharge(session, item), 0),
    0
  );

  const getSessionItemCount = (sessionId: string) => {
    return cart
      .find((session) => session.sessionId === sessionId)
      ?.cartItems.reduce((acc, item) => acc + item.qty, 0);
  };

  const getPieceCount = () => {
    let count = 0;
    selectedSession?.cartItems.forEach((item) => {
      count += item.qty * (item.garmentStyle?.pieceCount ?? 1);
    });
    return count.toString();
  };

  const getCartItemPrice = (sessionId: string, itemIndex: number) => {
    const session = cart.find((session) => session.sessionId === sessionId);
    const cartItem = session?.cartItems[itemIndex];
    if (cartItem)
      return itemTotalIn(session, cartItem);
    else return 0;
  };

  // Discount for one session, worked out on the lines it covers only
  // (see util/discount-calc).
  const getSessionDiscount = (
    discount: IDiscountResponse | null,
    session: ICartSession | null | undefined
  ) => {
    if (!discount || !session) return 0;
    return calculateDiscountAmount(
      getEligibleSessionAmount(session, discount),
      discount.discountMode,
      discount.discountAmount
    );
  };

  // Calculate discount amount
  const calculateTotalDiscountAmount = (discount: IDiscountResponse) =>
    cart.reduce((acc, session) => acc + getSessionDiscount(discount, session), 0);

  const calculateSelectedSessionDiscount = () =>
    getSessionDiscount(eligibleDiscounts, selectedSession);

  // Assign eligible discount
  useEffect(() => {
    // Editing an invoice: keep the discount it was created with (a dormant
    // one only while it meets its rules).
    if (discountLocked) {
      dispatch(
        DiscountSliceAction.setEligibleDiscount(
          resolveLockedDiscount(lockedDiscount, getDiscountByStoreResponse?.body ?? [], cartTotal)
        )
      );
      return;
    }

    // 0. Cashier explicitly removed the discount for this order
    if (discountRemoved) {
      dispatch(DiscountSliceAction.setEligibleDiscount(null));
      return;
    }

    // 1. Custom Discount Priority
    if (customDiscount) {
      dispatch(DiscountSliceAction.setEligibleDiscount(customDiscount));
      console.log("Custom Discount Applied");
      return;
    }

    // 2. Manual Selected Discount Priority
    if (selectedDiscount && getDiscountByStoreResponse) {
      const manualDiscount = getDiscountByStoreResponse.body.find(
        (discount) => String(discount.id) === String(selectedDiscount)
      );
      if (manualDiscount) {
        if (cartTotal < manualDiscount.minimumBillValue) {
          // Below the discount's minimum bill value: it can't be applied to
          // this cart, but it's still the customer's selected discount, so
          // don't clear selectedDiscount — just don't apply it.
          dispatch(DiscountSliceAction.setEligibleDiscount(null));
          console.log("Manual Discount Below Minimum Bill Value");
        } else {
          dispatch(DiscountSliceAction.setEligibleDiscount(manualDiscount));
          console.log("Manual Discount Applied");
        }
        return;
      }
    }

    // 3. Global Auto-Apply Priority
    if (getDiscountByStoreResponse && cartTotal) {
      const validDiscounts = getDiscountByStoreResponse.body.filter(
        (discount) => {
          // 1. Check enabled
          if (!discount.enabled) return false;

          // 2. Check Threshold (Global Rule - Must be met for ANY application)
          if (discount.minimumBillValue > cartTotal) return false;

          // 3. Check if Assigned to Current Customer
          const customerAssignedDiscountId = (customer && customer.discount && customer.discount !== 'none') ? String(customer.discount) : null;
          const isAssigned = customerAssignedDiscountId === String(discount.id);

          if (isAssigned) {
            return calculateTotalDiscountAmount(discount) > 0;
          }

          // 4. If NOT assigned, enforce Global Auto-Apply Rules
          if (!discount.autoApply) return false; // Must be marked for auto-apply
          if (!discount.applyToAnyUser) return false; // Must be applicable to anyone

          return calculateTotalDiscountAmount(discount) > 0;
        }
      );

      if (validDiscounts.length > 0) {
        // Find the one with max benefit
        const bestDiscount = validDiscounts.reduce((prev, current) => {
          return calculateTotalDiscountAmount(current) >
            calculateTotalDiscountAmount(prev)
            ? current
            : prev;
        });

        dispatch(DiscountSliceAction.setEligibleDiscount(bestDiscount));
        console.log("Auto Discount Applied");
      } else {
        dispatch(DiscountSliceAction.setEligibleDiscount(null));
        console.log("No Auto Discount Applied");
      }
    } else {
      dispatch(DiscountSliceAction.setEligibleDiscount(null));
      console.log("No Auto Discount Applied");
    }
  }, [
    cartTotal,
    getDiscountByStoreResponse,
    customer,
    selectedDiscount,
    customDiscount,
    discountRemoved,
    discountLocked,
    lockedDiscount,
    cart,
  ]);

  useEffect(() => {
    if (eligibleDiscounts) {
      const totalDiscountAmount =
        calculateTotalDiscountAmount(eligibleDiscounts);
      setTotalDiscountAmount(totalDiscountAmount);
    }
  }, [eligibleDiscounts]);

  useEffect(() => {
    const selectedSessionDiscount = calculateSelectedSessionDiscount();
    setSelectedSessionDiscount(selectedSessionDiscount);
  }, [eligibleDiscounts, selectedSession]);



  const getSessionDiscountById = (sessionId: string) =>
    getSessionDiscount(
      eligibleDiscounts,
      cart.find((session) => session.sessionId === sessionId)
    );
  const getSessionDiscountBySession = (session: ICartSession) =>
    getSessionDiscount(eligibleDiscounts, session);

  return {
    getPieceCount,
    sessionTotal,
    cartTotal,
    getSessionTotalBySessionId,
    getSessionItemCount,
    getCartItemPrice,
    totalDiscountAmount,
    selectedSessionDiscount,
    getSessionDiscountById,
    selectedDiscount,
    getSessionTotalBySession,
    getSessionDiscountBySession,
    cartSpecialCharge,
  };
};

export default useCart;
